/**
 * New Project wizard — the admin path from "Coffman won another job" to a
 * project that routes PDFs and already has a roster on it.
 *
 * Step 0 picks the job to copy FROM. Then five steps, in the order the
 * decisions actually get made: the project's own config, one step per thing
 * that can be carried over from the picked job (employees, equipment,
 * materials), then a read-back.
 *
 * Two things drive the shape of steps 2-4, and the source pick changes neither:
 *
 *  - Rosters are per project and copying writes NEW rows. Nothing stays linked,
 *    so editing either project later never touches the other.
 *  - Rates that differ per job get a human in the loop. Employee rates are
 *    typed here (step 2 makes you), so they need no reminder. Equipment rates
 *    are copied at their current value and cannot be typed for 200 assets in a
 *    modal, so step 3 says so loudly and the new project carries a banner in
 *    Settings until someone dismisses it.
 *
 * Mounted only while open (the caller conditionally renders it), so the fields
 * seed from `currentProject` once at mount and after that only when the PICKED
 * source changes. That matters: the settings project refetches on tab focus,
 * and an effect that re-seeded on a new `currentProject` identity would wipe
 * half-typed details the moment the admin alt-tabbed away and back.
 *
 * Everything the pick decides — the list's order, the prefill, the cleared
 * rates, the request body — is in `lib/newProjectWizard.ts`, which is where it
 * can be tested; this file renders it.
 */
import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { setActiveProjectId } from '../lib/activeProject';
import { formatMoneyCents } from '../lib/format';
import {
  buildCreateProjectRequest,
  copySourceList,
  copySourceNumber,
  copySourcePickMessage,
  copySourceRowMeta,
  findCopySource,
  resolvePrefill,
  type CopySourceOption,
} from '../lib/newProjectWizard';
import { HelpTip } from './HelpTip';
import { MoneyInput } from './MoneyInput';
import { Toggle } from './design';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import type {
  CopySource,
  CreateProjectResponse,
  NewProjectCopySelections,
  NewProjectDetails,
  SettingsProject,
} from '@coffman-tm/shared';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Step 0 is the copy source. Which job the roster, the contacts and the
   equipment rates come from is the one answer that changes what every later
   step means, so it is asked first and on its own screen. */
type WizardStep = 0 | 1 | 2 | 3 | 4 | 5;

const STEP_TITLES: Record<Exclude<WizardStep, 0>, string> = {
  1: 'Project details',
  2: 'Employees',
  3: 'Equipment',
  4: 'Materials',
  5: 'Review & create',
};

const centsToInput = (cents: number) => (cents / 100).toFixed(2);
const inputToCents = (raw: string) => Math.round(Number(raw) * 100);

/** A money field that must hold a real, non-negative amount. */
function moneyFilled(raw: string): boolean {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isFinite(n) && n >= 0;
}

/** A money field that must be strictly positive — the transferred-roster
 *  rates, where $0 would silently bill nothing on 60 employees at once
 *  (the server enforces the same > 0). */
function moneyPositive(raw: string): boolean {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isFinite(n) && n > 0;
}

function pctValid(raw: string): boolean {
  const n = Number(raw);
  return raw.trim() !== '' && Number.isFinite(n) && n >= 0 && n <= 100;
}

/**
 * Server failure codes as sentences. `project_number_exists` is absent on
 * purpose — the caller surfaces that one on the field rather than here.
 */
function createErrorMessage(err: unknown): string {
  if (err instanceof ApiCallError) {
    switch (err.payload.error) {
      case 'forbidden_not_admin':
        return 'Only an admin can create a project.';
      case 'missing_required_fields':
        return 'The server rejected the form for missing fields — check the project details step.';
      case 'invalid_email':
        return "One of the email addresses isn't valid.";
      case 'invalid_field_types':
        return 'One of the number fields came through as something other than a number.';
      case 'employee_rates_required':
        return 'Both employee rates are required when the roster is transferred.';
      case 'project_create_failed':
        return "The project wasn't created. Nothing was saved — try again.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Could not create the project.';
}

export function NewProjectWizard({
  onClose,
  currentProject,
}: {
  onClose: () => void;
  /**
   * The project the admin is standing in. It is the DEFAULT copy source and
   * the prefill of last resort (the one row the picker can always offer, even
   * with the list unreachable), and it is the one thing the source pick does
   * not move: the new project's system From-address is this project's, because
   * a sender has to be a domain Resend has verified.
   */
  currentProject: SettingsProject;
}) {
  const { token } = useAuth();

  const [step, setStep] = useState<WizardStep>(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateProjectResponse | null>(null);

  // ----- Step 0: which job to copy from -----
  // `null` sources means the list is still in flight. A failed load is not
  // fatal: the pick falls back to the job the admin is standing in, which is
  // the only source the wizard ever had before this step existed.
  const [sources, setSources] = useState<CopySource[] | null>(null);
  const [sourcesError, setSourcesError] = useState<string | null>(null);
  const [sourceId, setSourceId] = useState(currentProject.id);

  // ----- Step 1: project details -----
  // Blank: identity and the per-job money. Seeded: the config a sibling job
  // under the same GC genuinely shares.
  const [projectNumber, setProjectNumber] = useState('');
  const [numberError, setNumberError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [gcName, setGcName] = useState(currentProject.gc_name);
  const [gcEmail, setGcEmail] = useState(currentProject.gc_email);
  const [pmName, setPmName] = useState(currentProject.pm_name);
  const [pmEmail, setPmEmail] = useState(currentProject.pm_email);
  const [contract, setContract] = useState('');
  const [markupPct, setMarkupPct] = useState(String(currentProject.markup_pct));
  const [equipmentTaxPct, setEquipmentTaxPct] = useState(
    String(currentProject.equipment_tax_pct),
  );
  const [stRate, setStRate] = useState('');
  const [swingRate, setSwingRate] = useState('');
  const [perDiem, setPerDiem] = useState(centsToInput(currentProject.per_diem_rate_cents));
  const [perDiemEligible, setPerDiemEligible] = useState(
    currentProject.default_per_diem_eligible,
  );
  const [calcOtDt, setCalcOtDt] = useState(currentProject.calculate_ot_dt);
  // Whether the job pays a Sunday premium is configuration and carries; the
  // rate is a rate and starts blank, like the day and swing defaults above.
  const [sundayPremium, setSundayPremium] = useState(currentProject.sunday_premium_enabled);
  const [sundayRate, setSundayRate] = useState('');

  // ----- Steps 2-4: what to carry over -----
  const [copyEmployees, setCopyEmployees] = useState(false);
  const [empStRate, setEmpStRate] = useState('');
  const [empSwingRate, setEmpSwingRate] = useState('');
  const [copyEquipment, setCopyEquipment] = useState(false);
  const [copyMaterials, setCopyMaterials] = useState(false);

  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  /* Loaded once per open. A failure leaves the wizard fully usable with the
     standing job as the only option, so it sets an explanation rather than an
     error state — a New Project that cannot be created because a pick list
     didn't load would be the worse outcome. */
  useEffect(() => {
    if (!token) return;
    let live = true;
    api.projects
      .copySources(token)
      .then((res) => {
        if (live) setSources(res.sources);
      })
      .catch(() => {
        if (live) setSourcesError('other jobs');
      });
    return () => {
      live = false;
    };
  }, [token]);

  const sourceList = copySourceList(sources, currentProject);
  const chosen = findCopySource(sourceList, sourceId) ?? sourceList[0];
  const src = copySourceNumber(chosen, currentProject);

  /**
   * Seed the shared config from the chosen job, keyed on the ANSWER — so
   * walking back into step 1 never overwrites something half-typed, while
   * picking a different job does re-seed it. That config came from the source,
   * not from the admin.
   */
  const prefill = resolvePrefill(chosen);
  const prefilledFrom = useRef(prefill.key);
  useEffect(() => {
    // The ref is the ONLY gate. `prefill` is rebuilt on every render, so the
    // dep list below cannot stop this effect from running — it only stops
    // nothing. Everything in the body is safe to re-run, and is: it writes the
    // same values.
    if (prefilledFrom.current === prefill.key) return;
    prefilledFrom.current = prefill.key;
    const d = prefill.values;
    setGcName(d.gc_name);
    setGcEmail(d.gc_email);
    setPmName(d.pm_name);
    setPmEmail(d.pm_email);
    setMarkupPct(String(d.markup_pct));
    setEquipmentTaxPct(String(d.equipment_tax_pct));
    setPerDiem(centsToInput(d.per_diem_rate_cents));
    setPerDiemEligible(d.default_per_diem_eligible);
    setCalcOtDt(d.calculate_ot_dt);
    setSundayPremium(d.sunday_premium_enabled);
    /* Every rate CLEARED, never left alone. A rate typed against one job's
       contract must not follow the admin to another — it passes the
       blank-required gate the moment it holds a number, and the two employee
       rates are stamped onto every person the copy brings across. Safe to
       clear unconditionally: this only runs when the answer itself changed. */
    setStRate(prefill.rates.st);
    setSwingRate(prefill.rates.swing);
    setSundayRate(prefill.rates.sunday);
    setEmpStRate(prefill.rates.employeeSt);
    setEmpSwingRate(prefill.rates.employeeSwing);
  }, [prefill.key, prefill.values, prefill.rates]);

  /* Blank REQUIRED fields stay silent (Brandon 2026-08-05): the red * on
     each label is the standard everyone reads, and restating the first blank
     at the bottom was noise. They still disable Next. Only a FILLED-but-
     invalid value earns a sentence — that's the one thing an asterisk can't
     say. */
  const detailsMissing =
    projectNumber.trim() === '' ||
    name.trim() === '' ||
    gcName.trim() === '' ||
    gcEmail.trim() === '' ||
    pmName.trim() === '' ||
    pmEmail.trim() === '' ||
    markupPct.trim() === '' ||
    equipmentTaxPct.trim() === '' ||
    stRate.trim() === '' ||
    // Swing is optional (blank = day rate); the per-diem rate is required
    // only while the project pays per diem, same shape as Sunday premium.
    (perDiemEligible && perDiem.trim() === '') ||
    (sundayPremium && sundayRate.trim() === '');

  const detailsInvalid = ((): string | null => {
    if (gcEmail.trim() !== '' && !EMAIL_RE.test(gcEmail.trim()))
      return "The GC recipient email isn't a valid address.";
    if (pmEmail.trim() !== '' && !EMAIL_RE.test(pmEmail.trim()))
      return "The PM email isn't a valid address.";
    if (markupPct.trim() !== '' && !pctValid(markupPct))
      return 'Project markup must be between 0 and 100.';
    if (equipmentTaxPct.trim() !== '' && !pctValid(equipmentTaxPct))
      return 'Equipment tax must be between 0 and 100.';
    if (stRate.trim() !== '' && !moneyFilled(stRate))
      return 'Default employee rate must be a number.';
    if (swingRate.trim() !== '' && !moneyFilled(swingRate))
      return 'Default swing rate must be a number.';
    if (perDiemEligible && perDiem.trim() !== '' && !moneyFilled(perDiem))
      return 'Per-diem rate must be a number.';
    // $0 with the toggle on would create a job that claims a Sunday premium
    // and quietly bills Sundays at the day rate. The server refuses it too.
    if (sundayPremium && sundayRate.trim() !== '' && !moneyPositive(sundayRate))
      return 'Sunday premium rate must be above $0.';
    if (contract.trim() !== '' && !Number.isFinite(Number(contract)))
      return 'Original contract must be a number.';
    return null;
  })();

  const employeesMissing =
    copyEmployees && (empStRate.trim() === '' || empSwingRate.trim() === '');

  const employeesInvalid = ((): string | null => {
    if (!copyEmployees) return null;
    if (empStRate.trim() !== '' && !moneyPositive(empStRate))
      return 'The new day rate must be above $0.';
    if (empSwingRate.trim() !== '' && !moneyPositive(empSwingRate))
      return 'The new swing rate must be above $0.';
    return null;
  })();

  /* Step 5 re-checks steps 1 and 2 rather than trusting they were valid when
     it was reached — the 409 path sends the admin back into step 1 mid-flow.
     There a missing field DOES need a sentence: the asterisk is two steps
     behind you. */
  const detailsProblem =
    detailsInvalid ??
    (detailsMissing ? 'Fill in the required fields on the project details step.' : null);
  const employeesProblem =
    employeesInvalid ??
    (employeesMissing ? 'Enter both transferred-roster rates on the employees step.' : null);

  /** Non-null disables the forward button. */
  const stepBlocked =
    step === 1
      ? detailsProblem
      : step === 2
        ? employeesProblem
        : step === 5
          ? (detailsProblem ?? employeesProblem)
          : null;

  const sourcesLoading = sources === null && sourcesError === null;
  /* Clicking through the load window would configure a whole job off the
     default source having never been shown the others. Silent, like every
     other not-yet — there is a "Loading" line on screen saying why. */
  const forwardBlocked = stepBlocked !== null || (step === 0 && sourcesLoading);

  /** What actually renders under the form — invalid values only, except on
   *  the review step, where everything does. */
  const stepProblem =
    step === 1
      ? detailsInvalid
      : step === 2
        ? employeesInvalid
        : step === 5
          ? (detailsProblem ?? employeesProblem)
          : null;

  const submit = async () => {
    if (!token) return;
    const details: NewProjectDetails = {
      project_number: projectNumber.trim(),
      name: name.trim(),
      gc_name: gcName.trim(),
      gc_email: gcEmail.trim(),
      pm_name: pmName.trim(),
      pm_email: pmEmail.trim(),
      original_contract_amount_cents:
        contract.trim() === '' ? null : inputToCents(contract),
      markup_pct: Number(markupPct),
      equipment_tax_pct: Number(equipmentTaxPct),
      default_st_rate_cents: inputToCents(stRate),
      // Blank swing = the day rate. A swing line can arrive on any job (it
      // comes off the foreman's PDF, not config), so the stored default is a
      // real number, never a silent $0.
      default_swing_rate_cents:
        swingRate.trim() === '' ? inputToCents(stRate) : inputToCents(swingRate),
      // With eligibility off the rate field is hidden; the seeded value still
      // saves when present so flipping one employee eligible later has a real
      // rate to bill at, else 0 — Settings owns it from there.
      per_diem_rate_cents: perDiem.trim() === '' ? 0 : inputToCents(perDiem),
      default_per_diem_eligible: perDiemEligible,
      calculate_ot_dt: calcOtDt,
      sunday_premium_enabled: sundayPremium,
      // Off means no rate at all — the field is hidden in that state, so
      // whatever was typed before the toggle flipped back is not an answer.
      sunday_premium_rate_cents: sundayPremium ? inputToCents(sundayRate) : null,
    };
    const copy: NewProjectCopySelections = {
      employees: copyEmployees,
      // The two rate overrides are only meaningful with the roster; omit them
      // entirely otherwise rather than sending zeros the server must ignore.
      ...(copyEmployees
        ? {
            employee_st_rate_cents: inputToCents(empStRate),
            employee_swing_rate_cents: inputToCents(empSwingRate),
          }
        : {}),
      equipment: copyEquipment,
      materials: copyMaterials,
    };
    const body = buildCreateProjectRequest({
      details,
      copy,
      sourceProjectId: chosen.project_id,
      currentProjectId: currentProject.id,
    });

    setBusy(true);
    setErr(null);
    try {
      setCreated(await api.projects.create(token, body));
    } catch (e) {
      // A job-number clash is a fixable typo on one field, not a form-level
      // failure — put it on the field and take the admin back to it.
      if (e instanceof ApiCallError && e.payload.error === 'project_number_exists') {
        setNumberError(`Job ${projectNumber.trim()} already exists. Pick a different number.`);
        setStep(1);
        return;
      }
      // A refused SOURCE is the same shape of problem one step further back.
      // The refused job is dropped from the list on the way, so Next cannot
      // walk straight into the same refusal.
      const pickProblem =
        e instanceof ApiCallError ? copySourcePickMessage(e.payload.error) : null;
      if (pickProblem !== null) {
        setSources((prev) =>
          prev === null ? prev : prev.filter((s) => s.project_id !== chosen.project_id),
        );
        setSourceId(currentProject.id);
        setErr(pickProblem);
        setStep(0);
        return;
      }
      setErr(createErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Same switch the rail's project picker does: store the pick, hard-reload
   *  so every project-scoped cache in the tree is rebuilt from scratch. */
  const openNewProject = () => {
    if (!created) return;
    setActiveProjectId(created.project_id);
    window.location.assign('/');
  };

  const headTitle = created
    ? 'New project created'
    : step === 0
      ? 'New project'
      : `New project · Step ${step} of 5 — ${STEP_TITLES[step]}`;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Create a new project"
    >
      <div
        className="new-pco-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="pdf-modal-head">
          <div style={{ minWidth: 0 }}>
            <div
              className="pdf-modal-title"
              style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'visible' }}
            >
              {headTitle}
              {/* Orientation for the whole wizard, not just step 0 — the pick on
                  step 0 is what the tip explains, but the facts it states (rates
                  blank vs. verify-flagged) matter on every later step too. */}
              {!created && (
                <HelpTip title="Starting a new project" learnMore="new-project">
                  A new job starts as a copy of one you already have: its settings,
                  employees, equipment and materials. Rates are the exception — you enter
                  them on the new job and check them.
                </HelpTip>
              )}
            </div>
          </div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>

        <div className="new-pco-body">
          {created ? (
            <CreatedSummary
              created={created}
              sourceNumber={src}
              copiedEquipment={copyEquipment}
              onClose={onClose}
              onOpen={openNewProject}
            />
          ) : (
            <>
              {step === 0 && (
                <CopyFromStep
                  busy={busy}
                  list={sourceList}
                  currentProjectId={currentProject.id}
                  loading={sourcesLoading}
                  loadFailed={sourcesError !== null}
                  chosenId={chosen.project_id}
                  onChoose={(id) => {
                    setErr(null);
                    setSourceId(id);
                  }}
                />
              )}

              {step === 1 && (
                <DetailsStep
                  sourceNumber={src}
                  busy={busy}
                  projectNumber={projectNumber}
                  onProjectNumber={(v) => {
                    setProjectNumber(v);
                    setNumberError(null);
                  }}
                  numberError={numberError}
                  name={name}
                  onName={setName}
                  gcName={gcName}
                  onGcName={setGcName}
                  gcEmail={gcEmail}
                  onGcEmail={setGcEmail}
                  pmName={pmName}
                  onPmName={setPmName}
                  pmEmail={pmEmail}
                  onPmEmail={setPmEmail}
                  contract={contract}
                  onContract={setContract}
                  markupPct={markupPct}
                  onMarkupPct={setMarkupPct}
                  equipmentTaxPct={equipmentTaxPct}
                  onEquipmentTaxPct={setEquipmentTaxPct}
                  stRate={stRate}
                  onStRate={setStRate}
                  swingRate={swingRate}
                  onSwingRate={setSwingRate}
                  perDiem={perDiem}
                  onPerDiem={setPerDiem}
                  perDiemEligible={perDiemEligible}
                  onPerDiemEligible={setPerDiemEligible}
                  calcOtDt={calcOtDt}
                  onCalcOtDt={setCalcOtDt}
                  sundayPremium={sundayPremium}
                  onSundayPremium={setSundayPremium}
                  sundayRate={sundayRate}
                  onSundayRate={setSundayRate}
                />
              )}

              {step === 2 && (
                <EmployeesStep
                  sourceNumber={src}
                  busy={busy}
                  checked={copyEmployees}
                  onChecked={setCopyEmployees}
                  stRate={empStRate}
                  onStRate={setEmpStRate}
                  swingRate={empSwingRate}
                  onSwingRate={setEmpSwingRate}
                />
              )}

              {step === 3 && (
                <EquipmentStep
                  sourceNumber={src}
                  busy={busy}
                  checked={copyEquipment}
                  onChecked={setCopyEquipment}
                />
              )}

              {step === 4 && (
                <MaterialsStep
                  busy={busy}
                  checked={copyMaterials}
                  onChecked={setCopyMaterials}
                />
              )}

              {step === 5 && (
                <ReviewStep
                  sourceNumber={src}
                  projectNumber={projectNumber}
                  name={name}
                  gcName={gcName}
                  gcEmail={gcEmail}
                  pmName={pmName}
                  pmEmail={pmEmail}
                  contract={contract}
                  markupPct={markupPct}
                  equipmentTaxPct={equipmentTaxPct}
                  stRate={stRate}
                  swingRate={swingRate}
                  perDiem={perDiem}
                  perDiemEligible={perDiemEligible}
                  calcOtDt={calcOtDt}
                  sundayPremium={sundayPremium}
                  sundayRate={sundayRate}
                  copyEmployees={copyEmployees}
                  empStRate={empStRate}
                  empSwingRate={empSwingRate}
                  copyEquipment={copyEquipment}
                  copyMaterials={copyMaterials}
                />
              )}

              {/* This line IS the reason the forward button is dead, so it
                  says so. On its own it read as a floating instruction with
                  no obvious connection to the greyed-out Next beside it. */}
              {stepProblem && (
                <p style={hintStyle} role="status">
                  {step === 5
                    ? `Go back and fix this first: ${stepProblem}`
                    : `To continue: ${stepProblem}`}
                </p>
              )}
              {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

              <div className="new-pco-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => (step === 0 ? onClose() : setStep((s) => (s - 1) as WizardStep))}
                  disabled={busy}
                >
                  {step === 0 ? 'Cancel' : 'Back'}
                </button>
                {step < 5 ? (
                  <button
                    type="button"
                    className="btn btn-cta"
                    onClick={() => {
                      // Leaving step 1, carry its two defaults into step 2's
                      // roster rates if nothing has been typed there. They are
                      // different objects — one is the project default, the
                      // other is stamped on every transferred employee — but
                      // they are almost always the same number, and nobody
                      // should type it twice. Only ever seeds an empty field,
                      // so a rate already entered is never overwritten.
                      if (step === 1) {
                        if (empStRate === '') setEmpStRate(stRate);
                        // Blank swing default falls back to the day rate, so
                        // the roster seed follows the same rule.
                        if (empSwingRate === '') setEmpSwingRate(swingRate || stRate);
                      }
                      setStep((s) => (s + 1) as WizardStep);
                    }}
                    disabled={busy || forwardBlocked}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-green"
                    onClick={submit}
                    disabled={busy || forwardBlocked}
                  >
                    {busy ? 'Creating…' : 'Create project'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===========================================================================
 * Step 0 — which job to copy from
 * ======================================================================== */

function CopyFromStep({
  busy,
  list,
  currentProjectId,
  loading,
  loadFailed,
  chosenId,
  onChoose,
}: {
  busy: boolean;
  /** Always at least the job the admin is standing in. */
  list: CopySourceOption[];
  currentProjectId: string;
  loading: boolean;
  loadFailed: boolean;
  chosenId: string;
  onChoose: (projectId: string) => void;
}) {
  const labelId = useId();
  return (
    <>
      <p style={introStyle}>Pick a job to copy from.</p>

      {loading && (
        <p style={hintStyle} role="status">
          Loading the other jobs&hellip;
        </p>
      )}
      {/* Plain hint, not red: nothing is broken. The wizard still works with
          the standing job as its source, which is the only source it ever had. */}
      {loadFailed && (
        <p style={hintStyle} role="status">
          The other jobs couldn&rsquo;t be loaded, so only this one is offered. Close this
          and reopen it to try again.
        </p>
      )}

      {/* Label and list in one block: the body's 14px gap would otherwise
          float the label halfway to the paragraph above it. Same shape as
          ReviewGroup. */}
      <div>
        {/* The list's name on screen, and the same words the listbox
            announces — "Copy from" is what the help topic sends the PM
            looking for. */}
        <div className="field-label" id={labelId} style={{ marginBottom: 2 }}>
          Copy from
        </div>
        {/* Renders downward, in flow, like the inbox roster picker — no
            floating popover to be clipped by the modal. */}
        <div className="roster-picker-list" role="listbox" aria-labelledby={labelId}>
          {list.map((o) => {
            const meta = copySourceRowMeta(o);
            return (
              <button
                key={o.project_id}
                type="button"
                role="option"
                aria-selected={o.project_id === chosenId}
                className={`roster-picker-opt${o.project_id === chosenId ? ' selected' : ''}`}
                onClick={() => onChoose(o.project_id)}
                disabled={busy}
              >
                <span style={{ display: 'block' }}>
                  {o.project_number} · {o.name}
                  {o.project_id === currentProjectId && (
                    <span style={rowMetaStyle}> · the job you&rsquo;re in</span>
                  )}
                </span>
                {/* Absent when the counts were never loaded — three zeros
                    would read as a promise about what copying writes. */}
                {meta && <span style={{ ...rowMetaStyle, display: 'block' }}>{meta}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ===========================================================================
 * Step 1 — project details. Same field semantics and labels as the Project
 * Details settings form, so the two read identically.
 * ======================================================================== */

function DetailsStep(props: {
  sourceNumber: string;
  busy: boolean;
  projectNumber: string;
  onProjectNumber: (v: string) => void;
  numberError: string | null;
  name: string;
  onName: (v: string) => void;
  gcName: string;
  onGcName: (v: string) => void;
  gcEmail: string;
  onGcEmail: (v: string) => void;
  pmName: string;
  onPmName: (v: string) => void;
  pmEmail: string;
  onPmEmail: (v: string) => void;
  contract: string;
  onContract: (v: string) => void;
  markupPct: string;
  onMarkupPct: (v: string) => void;
  equipmentTaxPct: string;
  onEquipmentTaxPct: (v: string) => void;
  stRate: string;
  onStRate: (v: string) => void;
  swingRate: string;
  onSwingRate: (v: string) => void;
  perDiem: string;
  onPerDiem: (v: string) => void;
  perDiemEligible: boolean;
  onPerDiemEligible: (v: boolean) => void;
  calcOtDt: boolean;
  onCalcOtDt: (v: boolean) => void;
  sundayPremium: boolean;
  onSundayPremium: (v: boolean) => void;
  sundayRate: string;
  onSundayRate: (v: string) => void;
}) {
  const { busy } = props;
  return (
    <>
      {/* One line, not a paragraph: which job these values came off is the
          only thing step 0's answer still needs to say here, and after a
          job-number clash sends the admin back the pick is off screen. */}
      <p style={subHintStyle}>Prefilled from {props.sourceNumber}.</p>
      <div className="new-pco-field">
        <label className="field-label">
          Project number
          <RequiredMark />
        </label>
        <input
          className="field-input"
          value={props.projectNumber}
          onChange={(e) => props.onProjectNumber(e.target.value)}
          autoFocus
          disabled={busy}
          placeholder="26114"
          aria-required
        />
        {props.numberError && (
          <span style={{ color: 'var(--danger)', fontSize: 12, lineHeight: 1.4 }}>
            {props.numberError}
          </span>
        )}
        {/* Italic red on purpose (Brandon 2026-08-05): this is the one field
            that silently breaks intake if it's wrong, so it reads as a
            warning, not a hint. */}
        <span style={{ ...subHintStyle, color: 'var(--danger)', fontStyle: 'italic' }}>
          The HeavyJob job number. Inbound PDFs route by this, so it has to match the
          banner exactly.
        </span>
      </div>

      <TextField label="Project name" value={props.name} onChange={props.onName} busy={busy} required />

      <div className="new-pco-row">
        <TextField
          label="General contractor"
          value={props.gcName}
          onChange={props.onGcName}
          busy={busy}
          required
        />
        <TextField
          label="GC recipient email"
          value={props.gcEmail}
          onChange={props.onGcEmail}
          busy={busy}
          required
        />
      </div>
      <div className="new-pco-row">
        <TextField
          label="Project manager"
          value={props.pmName}
          onChange={props.onPmName}
          busy={busy}
          required
        />
        <TextField
          label="PM email (Reply-To + CC)"
          value={props.pmEmail}
          onChange={props.onPmEmail}
          busy={busy}
          required
        />
      </div>

      <MoneyField
        label="Original contract"
        value={props.contract}
        onChange={props.onContract}
        busy={busy}
        hint="Leave blank if the contract value isn't known yet."
      />

      <div className="new-pco-row">
        <TextField
          label="Project Markup (%)"
          value={props.markupPct}
          onChange={props.onMarkupPct}
          busy={busy}
          required
        />
        <TextField
          label="Equipment Tax (%)"
          value={props.equipmentTaxPct}
          onChange={props.onEquipmentTaxPct}
          busy={busy}
          required
        />
      </div>

      <div className="new-pco-row">
        <MoneyField
          label="Default employee rate"
          value={props.stRate}
          onChange={props.onStRate}
          busy={busy}
          required
        />
        {/* Not a given (Brandon 2026-08-05): a job may run no swing shift.
            Blank falls back to the day rate — a real number, never a silent
            $0 — because swing-ness comes off the foreman's PDF row, not
            project config, so a swing line can arrive on any job. */}
        <MoneyField
          label="Default swing rate"
          value={props.swingRate}
          onChange={props.onSwingRate}
          busy={busy}
          hint="Leave blank to use the day rate."
        />
      </div>

      {/* Per-diem reads like Sunday premium now (Brandon 2026-08-05): the
          project either pays it or it doesn't, and the rate only appears when
          it does. Per-person exceptions stay in Settings → Employees. */}
      <ToggleField
        label="Default per-diem eligible"
        value={props.perDiemEligible}
        onChange={props.onPerDiemEligible}
        busy={busy}
      />
      {props.perDiemEligible && (
        <MoneyField
          label="Per-diem rate"
          value={props.perDiem}
          onChange={props.onPerDiem}
          busy={busy}
          required
        />
      )}
      <ToggleField
        label="Calculate OT & DT"
        value={props.calcOtDt}
        onChange={props.onCalcOtDt}
        busy={busy}
      />
      {/* Config carries from the source job; the RATE never does — same rule
          the day and swing defaults follow. */}
      <ToggleField
        label="Sunday premium"
        value={props.sundayPremium}
        onChange={props.onSundayPremium}
        busy={busy}
      />
      {props.sundayPremium && (
        <MoneyField
          label="Sunday premium rate"
          value={props.sundayRate}
          onChange={props.onSundayRate}
          busy={busy}
          required
        />
      )}
    </>
  );
}

/* ===========================================================================
 * Step 2 — employees
 * ======================================================================== */

function EmployeesStep({
  sourceNumber,
  busy,
  checked,
  onChecked,
  stRate,
  onStRate,
  swingRate,
  onSwingRate,
}: {
  sourceNumber: string;
  busy: boolean;
  checked: boolean;
  onChecked: (v: boolean) => void;
  stRate: string;
  onStRate: (v: string) => void;
  swingRate: string;
  onSwingRate: (v: string) => void;
}) {
  return (
    <>
      <CopyCheckbox
        label={`Transfer all employees from ${sourceNumber}`}
        checked={checked}
        onChange={onChecked}
        busy={busy}
      />

      {checked && (
        <>
          <div className="new-pco-row">
            <MoneyField label="New day rate" value={stRate} onChange={onStRate} busy={busy} required />
            <MoneyField
              label="New swing rate"
              value={swingRate}
              onChange={onSwingRate}
              busy={busy}
              required
            />
          </div>
          <p style={hintStyle}>Applied to every transferred employee.</p>
        </>
      )}
    </>
  );
}

/* ===========================================================================
 * Step 3 — equipment
 * ======================================================================== */

function EquipmentStep({
  sourceNumber,
  busy,
  checked,
  onChecked,
}: {
  sourceNumber: string;
  busy: boolean;
  checked: boolean;
  onChecked: (v: boolean) => void;
}) {
  return (
    <>
      <CopyCheckbox
        label="Transfer all equipment with current rates"
        checked={checked}
        onChange={onChecked}
        busy={busy}
      />

      {checked && (
        <div style={warnPanelStyle}>
          <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
            Equipment rates may differ per job.
          </strong>{' '}
          Every asset comes across at its current {sourceNumber} rate — verify them on the
          new job&rsquo;s Equipment tab before the first rollup goes out.
        </div>
      )}
    </>
  );
}

/* ===========================================================================
 * Step 4 — materials
 * ======================================================================== */

function MaterialsStep({
  busy,
  checked,
  onChecked,
}: {
  busy: boolean;
  checked: boolean;
  onChecked: (v: boolean) => void;
}) {
  return (
    <>
      <CopyCheckbox
        label="Copy the materials list with default rates"
        checked={checked}
        onChange={onChecked}
        busy={busy}
      />
    </>
  );
}

/* ===========================================================================
 * Step 5 — review
 * ======================================================================== */

function ReviewStep(props: {
  sourceNumber: string;
  projectNumber: string;
  name: string;
  gcName: string;
  gcEmail: string;
  pmName: string;
  pmEmail: string;
  contract: string;
  markupPct: string;
  equipmentTaxPct: string;
  stRate: string;
  swingRate: string;
  perDiem: string;
  perDiemEligible: boolean;
  calcOtDt: boolean;
  sundayPremium: boolean;
  sundayRate: string;
  copyEmployees: boolean;
  empStRate: string;
  empSwingRate: string;
  copyEquipment: boolean;
  copyMaterials: boolean;
}) {
  const money = (raw: string) =>
    raw.trim() === '' ? '—' : formatMoneyCents(inputToCents(raw), { precise: true });

  return (
    <>
      {/* All values in the review read regular sans (Brandon 2026-08-05) —
          nothing is being edited here, so nothing needs the data voice. */}
      <ReviewGroup title="Project details">
        <ReviewRow label="Project number" value={props.projectNumber.trim()} />
        <ReviewRow label="Project name" value={props.name.trim()} />
        <ReviewRow label="General contractor" value={props.gcName.trim()} />
        <ReviewRow label="GC recipient email" value={props.gcEmail.trim()} />
        <ReviewRow label="Project manager" value={props.pmName.trim()} />
        <ReviewRow label="PM email (Reply-To + CC)" value={props.pmEmail.trim()} />
        <ReviewRow label="Original contract" value={money(props.contract)} />
        <ReviewRow label="Project Markup" value={`${props.markupPct}%`} />
        <ReviewRow label="Equipment Tax" value={`${props.equipmentTaxPct}%`} />
        <ReviewRow label="Default employee rate" value={money(props.stRate)} />
        <ReviewRow
          label="Default swing rate"
          value={
            props.swingRate.trim() === ''
              ? `${money(props.stRate)} (day rate)`
              : money(props.swingRate)
          }
        />
        <ReviewRow
          label="Default per-diem eligible"
          value={props.perDiemEligible ? 'Yes' : 'No'}
        />
        {props.perDiemEligible && (
          <ReviewRow label="Per-diem rate" value={money(props.perDiem)} />
        )}
        <ReviewRow label="Calculate OT & DT" value={props.calcOtDt ? 'Yes' : 'No'} />
        <ReviewRow label="Sunday premium" value={props.sundayPremium ? 'On' : 'Off'} />
        {/* The rate only exists while the premium is on — off, there is no
            number to review. */}
        {props.sundayPremium && (
          <ReviewRow label="Sunday premium rate" value={money(props.sundayRate)} />
        )}
      </ReviewGroup>

      <ReviewGroup title={`Carried over from ${props.sourceNumber}`}>
        <ReviewRow
          label="Employees"
          value={props.copyEmployees ? 'All active employees' : 'Not copied'}
        />
        {props.copyEmployees && (
          <>
            <ReviewRow label="New day rate (all)" value={money(props.empStRate)} />
            <ReviewRow label="New swing rate (all)" value={money(props.empSwingRate)} />
          </>
        )}
        <ReviewRow
          label="Equipment"
          value={props.copyEquipment ? 'All active assets, at current rates' : 'Not copied'}
        />
        <ReviewRow
          label="Materials"
          value={props.copyMaterials ? 'Price list with default rates' : 'Not copied'}
        />
      </ReviewGroup>

    </>
  );
}

/* ===========================================================================
 * Success — what actually got written
 * ======================================================================== */

function CreatedSummary({
  created,
  sourceNumber,
  copiedEquipment,
  onClose,
  onOpen,
}: {
  created: CreateProjectResponse;
  /** The job the copies came off — the same number every earlier step named. */
  sourceNumber: string;
  /** Whether the equipment box was checked, so the reminder only shows when
   *  there are copied rates to verify. */
  copiedEquipment: boolean;
  onClose: () => void;
  onOpen: () => void;
}) {
  /* Read defensively. This screen is the one place in the wizard where a
     missing field would throw AFTER the project was already written — the
     admin would see a crash for a create that succeeded, and retry it. */
  const count = (n: number | undefined) => String(typeof n === 'number' ? n : 0);
  const copied = created.copied ?? { employees: 0, equipment: 0, materials: 0, members: 0 };
  return (
    <>
      <p style={introStyle}>
        Project <strong>{created.project_number}</strong> is live.
      </p>

      <ReviewGroup title={`Copied over from ${sourceNumber}`}>
        <ReviewRow label="Employees" value={count(copied.employees)} />
        <ReviewRow label="Equipment" value={count(copied.equipment)} />
        <ReviewRow label="Materials" value={count(copied.materials)} />
        <ReviewRow label="Team members with access" value={count(copied.members)} />
      </ReviewGroup>

      <p style={introStyle}>
        PDFs with job {created.project_number} in the banner will now route here.
      </p>

      {created.copy_warning && (
        <div style={warnPanelStyle}>
          <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
            Heads up:
          </strong>{' '}
          {created.copy_warning}
        </div>
      )}

      {copiedEquipment && copied.equipment > 0 && (
        <div style={warnPanelStyle}>
          <strong style={{ color: 'var(--warning)', fontWeight: 600 }}>
            Verify the equipment rates.
          </strong>{' '}
          All {count(copied.equipment)} assets carry their {sourceNumber} rate until
          someone checks them. Settings &rarr; Equipment on the new project has a reminder
          banner with the same warning.
        </div>
      )}

      <p style={subHintStyle}>
        The project appears in the switcher in the side rail on your next page load.
      </p>

      <div className="new-pco-actions">
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
        <button type="button" className="btn btn-green" onClick={onOpen}>
          Open project {created.project_number}
        </button>
      </div>
    </>
  );
}

/* ===========================================================================
 * Field shells
 * ======================================================================== */

/** Red asterisk on a required field's label — the convention everyone reads
 *  (Brandon 2026-08-05); the input carries aria-required for the same fact. */
function RequiredMark() {
  // Sized against the field text, not the 10px eyebrow label it sits in —
  // at label size it read as a red speck (Brandon 2026-08-05).
  return (
    <span
      aria-hidden
      style={{ color: 'var(--danger)', fontSize: 14, fontWeight: 700, lineHeight: 1 }}
    >
      {' '}
      *
    </span>
  );
}

function TextField({
  label,
  value,
  onChange,
  busy,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  required?: boolean;
}) {
  return (
    <div className="new-pco-field">
      <label className="field-label">
        {label}
        {required && <RequiredMark />}
      </label>
      <input
        className="field-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
        aria-required={required || undefined}
      />
    </div>
  );
}

function MoneyField({
  label,
  value,
  onChange,
  busy,
  hint,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  busy: boolean;
  hint?: string;
  required?: boolean;
}) {
  return (
    <div className="new-pco-field">
      <label className="field-label">
        {label}
        {required && <RequiredMark />}
      </label>
      <MoneyInput
        className="field-input"
        value={value}
        onChange={onChange}
        disabled={busy}
      />
      {hint && <span style={subHintStyle}>{hint}</span>}
    </div>
  );
}

function ToggleField({
  label,
  value,
  onChange,
  busy,
  hint,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  busy: boolean;
  hint?: string;
}) {
  return (
    <div className="new-pco-field">
      <label className="field-label">{label}</label>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <Toggle value={value} onChange={onChange} disabled={busy} aria-label={label} />
        <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>{value ? 'Yes' : 'No'}</span>
      </span>
      {hint && <span style={subHintStyle}>{hint}</span>}
    </div>
  );
}

/** The one control each of steps 2-4 is really about. */
function CopyCheckbox({
  label,
  checked,
  onChange,
  busy,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  busy: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        fontSize: 13.5,
        fontWeight: 500,
        color: 'var(--ink)',
        lineHeight: 1.4,
        cursor: busy ? 'not-allowed' : 'pointer',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={busy}
        style={{ marginTop: 2 }}
      />
      <span>{label}</span>
    </label>
  );
}

function ReviewGroup({ title, children }: { title: string; children: React.ReactNode }) {
  // `field-label` is only an eyebrow inside `.new-pco-field`; bare, it renders
  // as 13px body text and the group title read as one more review row. A
  // section-weight sans title with room under it is what separates the groups.
  return (
    <div>
      <div style={reviewGroupTitleStyle}>{title}</div>
      {children}
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  // Always the sans voice — the whole wizard is mono-free (Brandon
  // 2026-08-05); nothing here is being edited.
  return (
    <div className="field-row" style={{ padding: '7px 0' }}>
      <div className="fr-label">{label}</div>
      <div className="fr-value serif">{value || '—'}</div>
    </div>
  );
}

// The review's group titles: the `.section-title` weight and size (index.css),
// which is scoped under `.section` and so cannot be reached by class here.
const reviewGroupTitleStyle: React.CSSProperties = {
  fontFamily: 'var(--sans)',
  fontSize: 15,
  fontWeight: 500,
  letterSpacing: '-0.005em',
  color: 'var(--ink)',
  paddingTop: 6,
  marginBottom: 4,
};

const introStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--ink-2)',
  lineHeight: 1.5,
  margin: 0,
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-2)',
  lineHeight: 1.45,
  margin: 0,
};

const subHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-3)',
  lineHeight: 1.4,
};

/** The quiet half of a pick-list row. Resets the weight and color
 *  `.roster-picker-opt.selected` puts on the row it sits in. */
const rowMetaStyle: React.CSSProperties = {
  color: 'var(--ink-3)',
  fontWeight: 400,
  fontSize: 11.5,
};

/** Filled amber panel — the same treatment as the bucket-clear warning: a
 *  consequence the admin must not skim past, not a muted aside. */
const warnPanelStyle: React.CSSProperties = {
  margin: 0,
  padding: '10px 12px',
  border: '1px solid var(--warning)',
  borderRadius: 6,
  background: 'var(--warning-tint)',
  fontFamily: 'var(--sans)',
  fontSize: 12,
  lineHeight: 1.45,
  color: 'var(--ink)',
};
